import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Users, Search, Star, Ban, StickyNote, Loader2, X,
  CalendarDays, Phone, Mail, ChevronRight, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newNote, setNewNote] = useState('');

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await api.get('/resa/customers');
      setCustomers(res.data || []);
    } catch { setCustomers([]); }
  }, []);

  useEffect(() => {
    fetchCustomers().finally(() => setLoading(false));
  }, [fetchCustomers]);

  const loadDetail = async (email) => {
    setSelected(email);
    setDetailLoading(true);
    try {
      const res = await api.get(`/resa/customers/${encodeURIComponent(email)}`);
      setDetail(res.data);
    } catch { setDetail(null); }
    setDetailLoading(false);
  };

  const toggleVip = async (email) => {
    try {
      const res = await api.put(`/resa/customers/${encodeURIComponent(email)}/vip`);
      toast.success(res.data.is_vip ? 'Client VIP' : 'VIP retire');
      fetchCustomers();
      if (detail) setDetail(d => ({ ...d, is_vip: res.data.is_vip }));
    } catch { toast.error('Erreur'); }
  };

  const toggleBlacklist = async (email) => {
    try {
      const res = await api.put(`/resa/customers/${encodeURIComponent(email)}/blacklist`);
      toast.success(res.data.is_blacklisted ? 'Client blackliste' : 'Blacklist retiree');
      fetchCustomers();
      if (detail) setDetail(d => ({ ...d, is_blacklisted: res.data.is_blacklisted }));
    } catch { toast.error('Erreur'); }
  };

  const addNote = async () => {
    if (!newNote || !selected) return;
    try {
      await api.post(`/resa/customers/${encodeURIComponent(selected)}/notes`, { text: newNote });
      setNewNote('');
      toast.success('Note ajoutee');
      loadDetail(selected);
    } catch { toast.error('Erreur'); }
  };

  const filtered = search
    ? customers.filter(c =>
        (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.phone || '').includes(search)
      )
    : customers;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-customers">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Clients</h1>
          <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">{customers.length} clients</span>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
        <Input value={search} onChange={e => setSearch(e.target.value)}
          className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] pl-9 h-9 text-xs"
          placeholder="Rechercher par nom, email ou telephone..." data-testid="search-customers" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* List */}
        <div className="lg:col-span-2 space-y-1" data-testid="customers-list">
          {filtered.length === 0 ? (
            <div className="lm-card p-8 text-center"><p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucun client</p></div>
          ) : filtered.map(c => (
            <button key={c.email} onClick={() => loadDetail(c.email)}
              className={`w-full text-left lm-card p-3 flex items-center gap-3 transition-all ${selected === c.email ? 'ring-1 ring-[rgb(var(--lm-gold)/0.4)]' : 'hover:bg-[rgb(var(--lm-surface-elevated)/0.5)]'}`}
              data-testid={`customer-${c.email}`}>
              {/* Avatar */}
              <div className="w-9 h-9 rounded-full bg-[rgb(var(--lm-surface-elevated))] flex items-center justify-center text-xs font-mono text-[rgb(var(--lm-text-muted))] shrink-0">
                {(c.name || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-[rgb(var(--lm-text))] font-medium truncate">{c.name}</span>
                  {c.is_vip && <Star size={10} className="text-[rgb(var(--lm-gold))] fill-[rgb(var(--lm-gold))]" />}
                  {c.is_blacklisted && <Ban size={10} className="text-[rgb(var(--lm-red))]" />}
                </div>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))] truncate">{c.email}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-mono text-[rgb(var(--lm-gold))]">{c.total_visits} visites</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{c.total_covers} couverts</p>
              </div>
              <ChevronRight size={14} className="text-[rgb(var(--lm-text-muted))] shrink-0" />
            </button>
          ))}
        </div>

        {/* Detail */}
        <div data-testid="customer-detail">
          {detailLoading ? (
            <div className="lm-card p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-[rgb(var(--lm-gold))] mx-auto" /></div>
          ) : detail ? (
            <div className="space-y-3">
              <div className="lm-card p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-[rgb(var(--lm-surface-elevated))] flex items-center justify-center text-lg font-mono text-[rgb(var(--lm-text-muted))]">
                    {(detail.name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-sm font-title text-[rgb(var(--lm-text))]">{detail.name}</h3>
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{detail.email}</p>
                    {detail.phone && <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{detail.phone}</p>}
                  </div>
                </div>

                <div className="flex gap-2 mb-3">
                  <div className="flex-1 text-center p-2 bg-[rgb(var(--lm-bg))] rounded-sm">
                    <p className="text-lg font-mono text-[rgb(var(--lm-gold))]">{detail.total_visits}</p>
                    <p className="text-[8px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Visites</p>
                  </div>
                </div>

                {/* Badges */}
                <div className="flex gap-2">
                  <button onClick={() => toggleVip(detail.email)}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-sm text-xs transition-all ${
                      detail.is_vip ? 'bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-gold))]' : 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]'
                    }`} data-testid="toggle-vip">
                    <Star size={12} className={detail.is_vip ? 'fill-current' : ''} /> VIP
                  </button>
                  <button onClick={() => toggleBlacklist(detail.email)}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-sm text-xs transition-all ${
                      detail.is_blacklisted ? 'bg-red-500/10 text-[rgb(var(--lm-red))]' : 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]'
                    }`} data-testid="toggle-blacklist">
                    <Ban size={12} /> Blacklist
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div className="lm-card p-4">
                <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-2">Notes</p>
                <div className="flex gap-1 mb-2">
                  <Input value={newNote} onChange={e => setNewNote(e.target.value)}
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] h-8 text-xs flex-1"
                    placeholder="Ajouter une note..." data-testid="note-input"
                    onKeyDown={e => { if (e.key === 'Enter') addNote(); }} />
                  <Button onClick={addNote} size="sm" className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] h-8" data-testid="add-note">
                    <Plus size={12} />
                  </Button>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {(detail.notes || []).map((n, i) => (
                    <div key={i} className="p-2 bg-[rgb(var(--lm-bg))] rounded-sm">
                      <p className="text-xs text-[rgb(var(--lm-text-secondary))]">{n.text}</p>
                      <p className="text-[8px] text-[rgb(var(--lm-text-muted))] mt-0.5">{new Date(n.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Historique */}
              <div className="lm-card p-4">
                <p className="text-[9px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-2">Historique</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {(detail.reservations || []).slice(0, 10).map(r => (
                    <div key={r.id} className="flex items-center gap-2 text-xs py-1 border-b border-[rgb(var(--lm-border)/0.2)] last:border-0">
                      <span className="font-mono text-[rgb(var(--lm-gold))]">{r.date}</span>
                      <span className="text-[rgb(var(--lm-text-muted))]">{r.time_slot}</span>
                      <span className="text-[rgb(var(--lm-text-secondary))]">{r.guests_count}p</span>
                      <span className={`text-[9px] ml-auto ${r.status === 'confirmed' ? 'text-green-400' : r.status === 'canceled' ? 'text-red-400' : 'text-yellow-400'}`}>
                        {r.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="lm-card p-8 text-center">
              <Users size={20} className="mx-auto mb-2 text-[rgb(var(--lm-text-muted))]" />
              <p className="text-xs text-[rgb(var(--lm-text-muted))]">Selectionnez un client</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCustomers;
